import { Component } from "react";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { AppBar, Badge, Box, Menu, MenuItem, Paper, Toolbar, Typography } from "@mui/material";
import * as Sentry from "@sentry/browser";
import {
  AppIcon,
  AutoFixIcon,
  CloseIcon,
  ConfigIcon,
  GPSIcon,
  HistoryIcon,
  LoadingIcon,
  MenuDotsIcon,
  MenuIcon,
  PhoneIcon,
  RebootIcon,
  ResetIcon,
  RipcordIcon,
  SaveIcon,
  SiteIcon,
  SOSButtonIcon,
  SoundIcon,
  UndoIcon,
  WiFiIcon,
} from "../../utils/icons";
import { Flex } from "../../components/Container/Flex";
import { Bold, Comment, Text } from "../../utils/style/texts";
import { Colors, contrastColor } from "../../utils/style/colors";
import IconDivider from "../../components/IconDivider";
import Group from "./components/Group";
import ToolBarButton from "./components/ToolBarButton";
import { resolveSettingIcon } from "./settingIcon";
import * as styles from "./styles";
import {
  selectDevConV4,
  selectSelectedDevice,
  selectV4ArcDeviceIds,
  selectV4ArcLinkChecks,
} from "../../redux/selectors/device.selector";
import {
  selectAdminMode,
  selectCurrentUser,
} from "../../redux/selectors/app.selectors";
import {
  toggleDevCon as toggleDevConD,
  reboot as rebootD,
} from "../../redux/actions/device.actions";
import {
  startUpdateSettingsV4 as startUpdateSettingsV4D,
  migrateDeviceToV4 as migrateDeviceToV4D,
  runConsolidation as runConsolidationD,
  initiatePushConsolidation as initiatePushConsolidationD,
  restoreDefaultSettings as restoreDefaultSettingsD,
  rebootDeviceV4 as rebootDeviceV4D,
  reloadSettingsV4 as reloadSettingsV4D,
  checkV4ArcDeviceLink as checkV4ArcDeviceLinkD,
} from "../../redux/actions/deviceV4.actions";
import { isArcLinkPending, isHandledByArc } from "../../utils/arcLink";
import {
  generateDefaultValues,
  buildValidationSchema,
  parseSettings,
  collectSettingsChanges,
  countSettingChanges,
  isCustomViewsSetting,
  settingIdOf,
} from "./utils";
import { appPagesChangeRows } from "./CustomViews";
import injectReducer from "../../utils/injectReducer";
import injectSaga from "../../utils/injectSaga";
import { DAEMON } from "../../utils/constants";
import reducer from "./reducer";
import saga from "./saga";
import { ARC_MANAGED_CATEGORIES, DEVCON_V4 } from "./constants";
import {
  getVisibility as getVisibilityD,
  setGroupVisibility as setGroupVisibilityD,
  setSettingVisibility as setSettingVisibilityD,
} from "./actions";
import {
  selectHiddenGroups,
  selectHiddenSettings,
  selectVisibilityEditable,
  selectVisibilityEnforced,
  selectVisibilitySettled,
} from "./selectors";
import ManDownIcon from "../../components/Icon/ManDownIcon";
import DeviceSettingsError from "./components/DeviceSettingsError";
import ConfiguringAnimation from "../../components/ConfiguringAnimation";
import SelectDevicesForConfigDialog from "../../components/Dialogs/SelectDevicesForConfig";
import ConfirmDialog from "../../components/Dialogs/Confirm";
import SyncHistory from "./components/SyncHistory";
import ConfigSyncNotice from "./components/ConfigSyncNotice";

const icons = {
  general: ConfigIcon,
  wifi: WiFiIcon,
  sideButton: AppIcon,
  sound: SoundIcon,
  menu: MenuIcon,
  manDown: ManDownIcon,
  ripcord: RipcordIcon,
  sosButton: SOSButtonIcon,
  alarmNumbers: PhoneIcon,
  whiteListNumbers: PhoneIcon,
  outdoorPositioning: GPSIcon,
  indoorPositioning: SiteIcon,
};

// Feature-based section order; unknown categories fall to the end (stable).
const CATEGORY_ORDER = [
  "general",
  "wifi",
  "sound",
  "menu",
  "manDown",
  "ripcord",
  "sosButton",
  "remoteButtons",
  "alarmNumbers",
  "whiteListNumbers",
  "indoorPositioning",
  "outdoorPositioning",
  "periodicPositioning",
];
const categoryRank = (key) => {
  const i = CATEGORY_ORDER.indexOf(key);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

// Discard reverts to the reported value. An unreported list reverts to [] to keep
// its type; other missing values send "" (the API rejects undefined).
const revertValue = (c) =>
  c.schema?.type?.toLowerCase() === "array" && !Array.isArray(c.from)
    ? []
    : c.from == null
    ? ""
    : c.from;

const isArcManaged = (category) =>
  ARC_MANAGED_CATEGORIES.includes(category.split(".")[0]);

// Honour hides of the old account-level "customViews" section on general.appPages.
const LEGACY_HIDDEN_GROUPS = { "general.appPages": "customViews" };

function DevConV4({
  device,
  t,
  toggleDevCon,
  migrateDeviceToV4,
  settings,
  startUpdateSettingsV4,
  runConsolidation,
  initiatePushConsolidation,
  restoreDefaultSettings,
  reloadSettingsV4,
  reboot,
  rebootDeviceV4,
  adminMode,
  user,
  hiddenGroups,
  hiddenSettings,
  visibilityEditable,
  visibilityEnforced,
  visibilitySettled,
  getVisibility,
  setGroupVisibility,
  setSettingVisibility,
  v4ArcDeviceIds,
  v4ArcLinkChecks,
  checkV4ArcDeviceLink,
}) {
  const deviceId = device?.get("id");
  const deviceIdV4 = device?.get("uuid");
  const isSynchronising = device?.get("isSynchronising");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [askReboot, setAskReboot] = useState(false);
  const [rebootConfirm, setRebootConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [parsedConfig, initialValues, validationSchema, changes] = useMemo(() => {
    const parsed = parseSettings(settings);
    const initial = generateDefaultValues(settings);
    const validation = buildValidationSchema(settings);
    // Diff against the form's initial (desired) values; honors showOn/hideOn.
    const ch = collectSettingsChanges(settings, initial);
    return [parsed, initial, validation, ch];
  }, [settings]);
  // `enforced` renders the panel the way the account's own users see it;
  // `editable` adds the eye toggles for an account manager. A V4-native session
  // has no V3 accountId and names its account by uuid instead.
  const accountId = user?.accountId || user?.v4ScopedAccountId;
  useEffect(() => {
    if (accountId) getVisibility(accountId);
  }, [accountId, getVisibility]);

  const visibility = useMemo(() => {
    const isGroupHidden = (key) => hiddenGroups.has(key);
    // The policy is keyed by the dotted setting path (see fieldKey).
    const isSettingHidden = (fieldId) => {
      const key = settingIdOf(fieldId);
      return (
        hiddenSettings.has(key) ||
        (!!LEGACY_HIDDEN_GROUPS[key] &&
          hiddenGroups.has(LEGACY_HIDDEN_GROUPS[key]))
      );
    };
    // Hidden itself, or sitting under a hidden group (ids are dotted paths).
    const isOutOfSight = (id) => {
      if (isSettingHidden(id)) return true;
      const parts = id.split(".");
      for (let i = 1; i < parts.length; i += 1) {
        if (isGroupHidden(parts.slice(0, i).join("."))) return true;
      }
      return false;
    };
    return {
      enforced: visibilityEnforced,
      editable: visibilityEditable,
      isGroupHidden,
      isSettingHidden,
      isOutOfSight,
      toggleGroup: (key) => setGroupVisibility(key, isGroupHidden(key)),
      toggleSetting: (fieldId) => {
        const key = settingIdOf(fieldId);
        const legacy = LEGACY_HIDDEN_GROUPS[key];
        if (legacy && hiddenGroups.has(legacy)) {
          // Hidden through the old section: showing it clears both.
          setGroupVisibility(legacy, true);
          if (hiddenSettings.has(key)) setSettingVisibility(key, true);
          return;
        }
        setSettingVisibility(key, isSettingHidden(key));
      },
    };
  }, [
    visibilityEnforced,
    visibilityEditable,
    hiddenGroups,
    hiddenSettings,
    setGroupVisibility,
    setSettingVisibility,
  ]);

  // The badge, per-group counters, discard and the save dialog must only ever
  // mention settings the viewer can actually see.
  const visibleChanges = useMemo(
    () =>
      visibility.enforced
        ? changes.filter((c) => !visibility.isOutOfSight(c.id))
        : changes,
    [changes, visibility]
  );
  const changedSettings = useMemo(
    () => new Set(visibleChanges.map((c) => c.id)),
    [visibleChanges]
  );
  // id -> { from: reported, to: desired }; drives the per-field hover tooltip.
  const changeInfo = useMemo(
    () => new Map(visibleChanges.map((c) => [c.id, c])),
    [visibleChanges]
  );
  const changeCount = countSettingChanges(visibleChanges);
  // The save dialog lists each custom-view change on its own row.
  const dialogChanges = useMemo(
    () =>
      visibleChanges.flatMap((c) => {
        if (!isCustomViewsSetting(c.schema, c.category, c.name)) return [c];
        const rows = appPagesChangeRows(c, t);
        return rows.length ? rows : [c];
      }),
    [visibleChanges, t]
  );
  const saveTooltip = isSynchronising
    ? t("synchronising", { defaultValue: "Synchronising…" })
    : changeCount > 0
    ? t("syncNSettingsToDevice", {
        count: changeCount,
        defaultValue: "Sync {count} change(s) to the device",
      })
    : t("save");
  const onSubmit = useCallback(() => setAskReboot(true), []);
  // ARC-programmed emergency numbers are read-only. Wait for the link check
  // unless already linked; a failed check keeps the last known answer.
  const arcHandled = isHandledByArc(device, user, v4ArcDeviceIds);
  const arcLinkPending = isArcLinkPending(
    device,
    user,
    v4ArcDeviceIds,
    v4ArcLinkChecks
  );
  const deviceOwnerId = device?.get("v4OwnerId");
  const deviceAccountId = device?.get("accountId");
  useEffect(() => {
    if (deviceIdV4) {
      checkV4ArcDeviceLink(deviceIdV4, deviceOwnerId, deviceAccountId);
    }
  }, [deviceIdV4, deviceOwnerId, deviceAccountId, checkV4ArcDeviceLink]);
  const isLocked = (category) => arcHandled && isArcManaged(category);
  const discardableChanges = useMemo(
    () =>
      arcHandled
        ? visibleChanges.filter((c) => !isArcManaged(c.category))
        : visibleChanges,
    [arcHandled, visibleChanges]
  );
  // Discard unsaved changes: revert each changed field's desired value to the
  // device's reported value (see revertValue).
  const discardChanges = useCallback(() => {
    if (!discardableChanges.length) return;
    startUpdateSettingsV4(
      Object.fromEntries(
        discardableChanges.map((c) => [c.name, revertValue(c)])
      )
    );
  }, [discardableChanges, startUpdateSettingsV4]);
  const runConsolidationCb = useCallback((strategy) => {
    setMenuAnchor(null);
    runConsolidation(deviceIdV4, strategy);
  }, [deviceIdV4, runConsolidation]);
  // Repair path out of the error panel, where the toolbar menu is disabled.
  const pullSettings = useCallback(() => {
    runConsolidationCb("pull");
  }, [runConsolidationCb]);
  // A run finished while the panel is still in its error state.
  const reloadSettings = useCallback(() => {
    if (deviceIdV4) reloadSettingsV4(deviceIdV4);
  }, [deviceIdV4, reloadSettingsV4]);
  // Problems the panel ran into itself: a settings write the platform refused
  // as out of sync, or a sync run that failed. Both are repaired by a pull
  // consolidation today; the menu entry will move to the solve endpoint later.
  const hasIssue =
    !!device?.get("updatingSettingError") || !!device?.get("consolidationError");
  const isError = !!device?.get("migrationError") || !parsedConfig;
  const settingsError = (
    <DeviceSettingsError
      error={device?.get("migrationError")}
      deviceId={deviceIdV4}
      syncing={isSynchronising}
      onSync={pullSettings}
      onIdle={reloadSettings}
    />
  );
  // Wait for the policy before showing anyone a filtered configuration.
  const visibilityPending = visibilityEnforced && !!accountId && !visibilitySettled;
  const isLoading =
    device?.get("isMigratingV4") ||
    (!settings && !isError) ||
    visibilityPending ||
    arcLinkPending;

  useEffect(() => {
    if (deviceId) {
      if (!device?.get("isMigratingV4")) {
        migrateDeviceToV4(deviceId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  return (
    <styles.Wrapper>
      <ErrorBoundary deviceId={deviceId}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ submitForm, errors, touched }) => (
            <>
              <AppBar position="relative" color="default">
                <Toolbar variant="dense">
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <ToolBarButton
                      tooltip={t("close")}
                      icon={<CloseIcon color={Colors.gray50} />}
                      onClick={() => toggleDevCon(false)}
                    />
                  </Box>
                  <Flex col style={{ marginLeft: 5 }}>
                    <Bold>{device.get("name")}</Bold>
                    <Comment>{device.deviceTypeName(t)}</Comment>
                  </Flex>

                  <styles.RightControls isEnabled={!isLoading}>
                    <ToolBarButton
                      disabled={
                        isError ||
                        isSynchronising ||
                        !!device.get("updatingSettingError")
                      }
                      tooltip={saveTooltip}
                      icon={
                        isSynchronising ? (
                          <LoadingIcon color={Colors.gray50} />
                        ) : (
                          <Badge
                            badgeContent={changeCount}
                            sx={{
                              "& .MuiBadge-badge": {
                                backgroundColor: Colors.primary(),
                                color: contrastColor(Colors.primary()),
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                height: 16,
                                minWidth: 16,
                                padding: "0 4px",
                              },
                            }}
                          >
                            <SaveIcon
                              color={
                                changeCount > 0 ? Colors.primary() : Colors.gray50
                              }
                            />
                          </Badge>
                        )
                      }
                      onClick={submitForm}
                    />
                    {discardableChanges.length > 0 && (
                      <>
                        <IconDivider />
                        <ToolBarButton
                          disabled={isError || isSynchronising}
                          tooltip={t("discardChanges", {
                            defaultValue: "Discard changes",
                          })}
                          icon={<UndoIcon color={Colors.gray50} />}
                          onClick={discardChanges}
                        />
                      </>
                    )}
                    <IconDivider />
                    <ToolBarButton
                      disabled={isError || !deviceIdV4}
                      tooltip={t("history")}
                      icon={<HistoryIcon color={Colors.gray50} />}
                      onClick={() => setShowHistory(true)}
                    />
                    <IconDivider />
                    <ToolBarButton
                      disabled={isError || isSynchronising}
                      tooltip={t("more", { defaultValue: "More" })}
                      icon={<MenuDotsIcon color={Colors.gray50} />}
                      onClick={(e) => setMenuAnchor(e.currentTarget)}
                    />
                    {menuAnchor && (
                      <Menu
                        anchorEl={menuAnchor}
                        open
                        onClose={() => setMenuAnchor(null)}
                      >
                        {hasIssue && (
                          <MenuItem
                            disabled={!deviceIdV4}
                            onClick={() => runConsolidationCb("pull")}
                          >
                            <AutoFixIcon color="action" />
                            &nbsp;
                            <Text>
                              {t("automaticallySolveIssues", {
                                defaultValue: "Automatically solve issues",
                              })}
                            </Text>
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            setMenuAnchor(null);
                            restoreDefaultSettings(deviceIdV4);
                          }}
                        >
                          <ResetIcon color="action" />
                          &nbsp;
                          <Text>{t("restoreDeviceSettingsToDefaults")}</Text>
                        </MenuItem>
                        <MenuItem
                          disabled={
                            device?.isFromV4 ? !deviceIdV4 : !deviceId
                          }
                          onClick={() => {
                            setMenuAnchor(null);
                            setRebootConfirm(true);
                          }}
                        >
                          <RebootIcon color="action" />
                          &nbsp;
                          <Text>{t("reboot", { defaultValue: "Reboot" })}</Text>
                        </MenuItem>
                        {adminMode && (
                          <MenuItem
                            disabled={!deviceIdV4}
                            onClick={() => runConsolidationCb("pull")}
                          >
                            <ConfigIcon color="action" />
                            &nbsp;
                            <Text>
                              {t("consolidationPull", {
                                defaultValue: "Consolidation: pull",
                              })}
                            </Text>
                          </MenuItem>
                        )}
                        {adminMode && (
                          <MenuItem
                            disabled={!deviceIdV4}
                            onClick={() => runConsolidationCb("push")}
                          >
                            <ConfigIcon color="action" />
                            &nbsp;
                            <Text>
                              {t("consolidationPush", {
                                defaultValue: "Consolidation: push",
                              })}
                            </Text>
                          </MenuItem>
                        )}
                      </Menu>
                    )}
                  </styles.RightControls>
                </Toolbar>
              </AppBar>
              {isLoading ? (
                <Flex justify="center">
                  <ConfiguringAnimation />
                </Flex>
              ) : isError ? (
                settingsError
              ) : (
                <styles.Form>
                  {device?.isEmeritWatch && deviceIdV4 && (
                    <ConfigSyncNotice key={deviceIdV4} deviceId={deviceIdV4} />
                  )}
                  <Form>
                    {Object.entries(parsedConfig)
                      // Ripcord only applies to ripcord-capable Twigs; hide otherwise.
                      .filter(
                        ([category]) =>
                          category !== "ripcord" || device?.ripcordPossible
                      )
                      .sort(([a], [b]) => categoryRank(a) - categoryRank(b))
                      .map(([category, categoryConfig]) => (
                        <Group
                          // Remount FastFields when the lock flips.
                          key={isLocked(category) ? `${category}:locked` : category}
                          locked={isLocked(category)}
                          groupName={category}
                          headerText={t(category)}
                          settings={categoryConfig}
                          changedSettings={changedSettings}
                          changeInfo={changeInfo}
                          visibility={visibility}
                          hasError={!!errors[category] && !!touched[category]}
                          icon={
                            resolveSettingIcon(settings?.[category]?.icon) ||
                            icons[category]
                          }
                          onChange={(a, b) => {
                            if (typeof a === "object") {
                              startUpdateSettingsV4(a);
                            } else {
                              startUpdateSettingsV4({ [a]: b });
                            }
                          }}
                        />
                      )
                    )}
                  </Form>
                </styles.Form>
              )}
            </>
          )}
        </Formik>
      </ErrorBoundary>
      <SelectDevicesForConfigDialog
        isOpen={askReboot}
        device={device}
        diff={{ saveOnly: true, settingsChanges: dialogChanges }}
        onClose={() => {
          setAskReboot(false);
        }}
        onSaveV4={(deviceIds, onlyChanges, reboot) => {
          setAskReboot(false);
          initiatePushConsolidation(deviceIdV4, deviceIds, onlyChanges, reboot);
        }}
      />
      <ConfirmDialog
        open={rebootConfirm}
        text={t("rebootDevice?", { defaultValue: "Reboot this device?" })}
        onConfirm={() => {
          setRebootConfirm(false);
          if (device?.isFromV4) {
            // V4-native: reboot via the V4 command endpoint; V3/migrated use V3.
            rebootDeviceV4(deviceIdV4);
          } else {
            reboot(device);
          }
        }}
        onCancel={() => setRebootConfirm(false)}
      />
      <SyncHistory
        open={showHistory}
        deviceId={deviceIdV4}
        adminMode={adminMode}
        onClose={() => setShowHistory(false)}
      />
    </styles.Wrapper>
  );
}

export default compose(
  withTranslation(),
  injectReducer({ key: DEVCON_V4, reducer }),
  // DAEMON: the debounced save must survive closing the panel.
  injectSaga({ key: DEVCON_V4, saga, mode: DAEMON }),
  connect(
    createStructuredSelector({
      device: selectSelectedDevice(),
      settings: selectDevConV4(),
      adminMode: selectAdminMode(),
      user: selectCurrentUser(),
      v4ArcDeviceIds: selectV4ArcDeviceIds(),
      v4ArcLinkChecks: selectV4ArcLinkChecks(),
      hiddenGroups: selectHiddenGroups(),
      hiddenSettings: selectHiddenSettings(),
      visibilityEditable: selectVisibilityEditable(),
      visibilityEnforced: selectVisibilityEnforced(),
      visibilitySettled: selectVisibilitySettled(),
    }),
    {
      toggleDevCon: toggleDevConD,
      migrateDeviceToV4: migrateDeviceToV4D,
      startUpdateSettingsV4: startUpdateSettingsV4D,
      runConsolidation: runConsolidationD,
      initiatePushConsolidation: initiatePushConsolidationD,
      restoreDefaultSettings: restoreDefaultSettingsD,
      reloadSettingsV4: reloadSettingsV4D,
      reboot: rebootD,
      rebootDeviceV4: rebootDeviceV4D,
      getVisibility: getVisibilityD,
      setGroupVisibility: setGroupVisibilityD,
      setSettingVisibility: setSettingVisibilityD,
      checkV4ArcDeviceLink: checkV4ArcDeviceLinkD,
    }
  )
)(DevConV4);

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    Sentry.withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });
      Sentry.captureException(error);
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">
            Something went wrong. Please try again later or contact support.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Device ID: {this.props.deviceId}
          </Typography>
        </Paper>
      );
    }
    return this.props.children;
  }
}
